import React, { useState, useCallback, useRef, useEffect } from 'react';
import styles from './styles/TagSearch.module.css';
import { searchTags } from '../../../services/TagServices';

const DEBOUNCE_MS = 300;
const MAX_SUGGESTIONS = 8;

/**
 * Reusable tag combobox: selected tags render as pills inside a bordered control,
 * followed by a text input with live autocomplete suggestions (StackOverflow style).
 *
 * This component owns ONLY the search/suggestion UI. It never creates or persists
 * anything — it merely reports a chosen tag via `onSelectTag` and a removed tag via
 * `onRemoveTag`. The parent decides what those mean.
 *
 * Tag CREATION is opt-in and off by default: with `allowCreate` false, the bar can
 * only ever emit existing tag objects, never a free-text string.
 *
 * @param {(tag: object) => void} onSelectTag - Called when the user picks an existing
 *        suggestion (receives the tag object). When `allowCreate` is true and the typed
 *        text has no exact match, the "Create: X" option emits the trimmed string instead.
 * @param {object[]} [selectedTags=[]] - Already-chosen tags. Rendered as pills inside the
 *        control and filtered out of suggestions (matched by `_id`).
 * @param {(tag: object) => void} [onRemoveTag] - If provided, pills show a remove button
 *        and Backspace on an empty input removes the last selected tag.
 * @param {boolean} [allowCreate=false] - Show a "Create: X" option for text with no exact
 *        match. Leave false for search-only pickers; set true only where new tags are allowed.
 * @param {boolean} [disabled=false] - Disable the text input.
 * @param {string} [placeholder]
 * @param {boolean} [autoFocus=false]
 */
const TagSearch = ({
    onSelectTag,
    selectedTags = [],
    onRemoveTag,
    allowCreate = false,
    disabled = false,
    placeholder = 'Search tags...',
    autoFocus = false,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const inputRef = useRef(null);
    const debounceTimer = useRef(null);
    const wrapperRef = useRef(null);

    const trimmed = inputValue.trim();
    const hasExactMatch = suggestions.some(
        (s) => s.name.toLowerCase() === trimmed.toLowerCase()
    );
    const canCreateNew = allowCreate && trimmed.length > 0 && !hasExactMatch;
    const totalDropdownItems = suggestions.length + (canCreateNew ? 1 : 0);

    const fetchSuggestions = useCallback(
        (query) => {
            clearTimeout(debounceTimer.current);
            const q = query.trim();
            if (!q) {
                setSuggestions([]);
                setShowSuggestions(false);
                setIsLoading(false);
                return;
            }
            setShowSuggestions(true);
            debounceTimer.current = setTimeout(async () => {
                setIsLoading(true);
                try {
                    const results = await searchTags(q);
                    const excludeIds = new Set(selectedTags.map((t) => t._id));
                    setSuggestions(
                        results
                            .filter((r) => !excludeIds.has(r._id))
                            .slice(0, MAX_SUGGESTIONS)
                    );
                } catch (err) {
                    console.error('Error fetching tag suggestions:', err);
                    setSuggestions([]);
                } finally {
                    setIsLoading(false);
                }
            }, DEBOUNCE_MS);
        },
        [selectedTags]
    );

    const handleSelect = useCallback(
        (tagOrName) => {
            onSelectTag?.(tagOrName);
            setInputValue('');
            setSuggestions([]);
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        },
        [onSelectTag]
    );

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        setHighlightedIndex(-1);
        fetchSuggestions(val);
    };

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (showSuggestions)
                    setHighlightedIndex((i) => Math.min(i + 1, totalDropdownItems - 1));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex((i) => Math.max(i - 1, -1));
                return;
            }
            if (e.key === 'Escape') {
                setShowSuggestions(false);
                setHighlightedIndex(-1);
                return;
            }
            if ((e.key === 'Enter' || e.key === ',') && trimmed) {
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                    handleSelect(suggestions[highlightedIndex]);
                } else if (canCreateNew) {
                    handleSelect(trimmed);
                } else if (suggestions.length > 0) {
                    handleSelect(suggestions[0]);
                }
                return;
            }
            if (
                e.key === 'Backspace' &&
                !inputValue &&
                onRemoveTag &&
                selectedTags.length > 0
            ) {
                onRemoveTag(selectedTags[selectedTags.length - 1]);
            }
        },
        [
            showSuggestions,
            totalDropdownItems,
            trimmed,
            highlightedIndex,
            suggestions,
            canCreateNew,
            inputValue,
            handleSelect,
            onRemoveTag,
            selectedTags,
        ]
    );

    // Close the dropdown when clicking outside the component.
    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Cancel any pending debounced search on unmount.
    useEffect(() => () => clearTimeout(debounceTimer.current), []);

    return (
        <div className={styles.tagSearch} ref={wrapperRef}>
            <div
                className={`${styles.control} ${isFocused ? styles.focused : ''} ${
                    disabled ? styles.disabled : ''
                }`}
                onClick={() => inputRef.current?.focus()}
                tabIndex={-1}
            >
                {selectedTags.map((tag) => (
                    <span key={tag._id} className={styles.tagPill}>
                        <span className={styles.tagText}>{tag.name}</span>
                        {onRemoveTag && (
                            <button
                                type="button"
                                className={styles.removeTagButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveTag(tag);
                                }}
                                aria-label={`Remove tag ${tag.name}`}
                            >
                                ×
                            </button>
                        )}
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        setIsFocused(true);
                        if (trimmed) setShowSuggestions(true);
                    }}
                    onBlur={() => setIsFocused(false)}
                    className={styles.searchInput}
                    placeholder={selectedTags.length === 0 ? placeholder : ''}
                    aria-label="Search tags"
                    aria-autocomplete="list"
                    aria-expanded={showSuggestions}
                    spellCheck="false"
                    disabled={disabled}
                    autoFocus={autoFocus}
                />
            </div>

            {showSuggestions && (isLoading || suggestions.length > 0 || canCreateNew) && (
                <ul className={styles.suggestionsDropdown} role="listbox">
                    {isLoading ? (
                        <li className={styles.loadingItem}>Searching...</li>
                    ) : (
                        <>
                            {suggestions.map((suggestion, index) => (
                                <li
                                    key={suggestion._id}
                                    className={`${styles.suggestionItem} ${
                                        highlightedIndex === index ? styles.highlighted : ''
                                    }`}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelect(suggestion);
                                    }}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                    role="option"
                                    aria-selected={highlightedIndex === index}
                                >
                                    <span className={styles.suggestionName}>
                                        {suggestion.name}
                                    </span>
                                    {suggestion.usageCount != null && (
                                        <span className={styles.suggestionCount}>
                                            {suggestion.usageCount}
                                        </span>
                                    )}
                                </li>
                            ))}
                            {canCreateNew && (
                                <li
                                    className={`${styles.suggestionItem} ${styles.newTagOption} ${
                                        highlightedIndex === suggestions.length
                                            ? styles.highlighted
                                            : ''
                                    }`}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelect(trimmed);
                                    }}
                                    onMouseEnter={() => setHighlightedIndex(suggestions.length)}
                                    role="option"
                                    aria-selected={highlightedIndex === suggestions.length}
                                >
                                    Create: <strong>{trimmed}</strong>
                                </li>
                            )}
                        </>
                    )}
                </ul>
            )}
        </div>
    );
};

export default TagSearch;
